import { useState, useCallback, useEffect } from 'react';
import { getPropertyBids as fetchPropertyBidsFromSupabase } from '../services/supabaseService';

/**
 * Custom hook para gestionar datos de propiedades, ofertas e invitaciones
 * VERSIÓN CON SUPABASE + SOPORTE PARA CONDICIONES DE OFERTAS
 */
export const usePropertyData = (properties, bids, invites, onSaveProperties, onSaveBids, onSaveInvites) => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  
  // ✅ NUEVO: Estado para almacenar bids enriquecidos con condiciones
  const [enrichedBids, setEnrichedBids] = useState({});

  // ✅ NUEVO: Función para cargar bids con condiciones desde Supabase
  const refreshPropertyBids = useCallback(async (propertyId) => {
    try {
      const bidsWithConditions = await fetchPropertyBidsFromSupabase(propertyId);
      setEnrichedBids(prev => ({
        ...prev,
        [propertyId]: bidsWithConditions
      }));
    } catch (error) {
      console.error('Error cargando bids con condiciones:', error);
      // En caso de error, devolver bids del array normal
    }
  }, []);

  // Obtener ofertas de una propiedad específica
  // ✅ MODIFICADO: Primero busca en enrichedBids, si no existe usa el array normal
  const getPropertyBids = useCallback((propertyId) => {
    // Si ya tenemos bids enriquecidos para esta propiedad, usarlos
    if (enrichedBids[propertyId]) {
      return enrichedBids[propertyId];
    }
    
    // Fallback: filtrar del array normal (sin condiciones)
    return bids.filter(b => b.propertyId === propertyId).sort((a, b) => b.amount - a.amount || new Date(a.timestamp) - new Date(b.timestamp));
  }, [bids, enrichedBids]);

  // Obtener invitaciones de una propiedad específica
  const getPropertyInvites = useCallback((propertyId) => {
    return invites.filter(i => i.propertyId === propertyId);
  }, [invites]);

  // Eliminar propiedad y datos relacionados (ASYNC para Supabase)
  const deleteProperty = useCallback(async (propertyId) => {
    // Sin confirmación - se maneja en el modal de AdminDashboard
    const newProperties = properties.filter(p => p.id !== propertyId);
    const newBids = bids.filter(b => b.propertyId !== propertyId);
    const newInvites = invites.filter(i => i.propertyId !== propertyId);

    // ✅ NUEVO: Limpiar bids enriquecidos de la propiedad eliminada
    setEnrichedBids(prev => {
      const updated = { ...prev };
      delete updated[propertyId];
      return updated;
    });

    // Llamadas async a Supabase vía callbacks
    await onSaveProperties(newProperties);
    await onSaveBids(newBids);
    await onSaveInvites(newInvites);
  }, [properties, bids, invites, onSaveProperties, onSaveBids, onSaveInvites]);

  // Calcular estadísticas generales
  const getStats = useCallback(() => {
    const totalBidders = new Set(bids.map(b => b.buyerEmail)).size;
    
    return {
      totalProperties: properties.length,
      totalBids: bids.length,
      totalInvites: invites.length,
      totalBidders
    };
  }, [properties.length, bids, invites.length]);

  return {
    selectedProperty,
    setSelectedProperty,
    getPropertyBids,
    getPropertyInvites,
    deleteProperty,
    getStats,
    refreshPropertyBids, // ✅ NUEVO: Exportar función para enriquecer bids
  };
};